import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { Children, ReactNode, useEffect, useState, Ref, useId, useRef } from 'react'
// Import Swiper React components
import { Swiper as SwiperInterface } from 'swiper'
import { FreeMode, Mousewheel, Navigation, Pagination, Virtual } from 'swiper/modules'
import { Swiper as SwiperC, SwiperRef, SwiperSlide } from 'swiper/react'
import 'swiper/scss'
import 'swiper/scss/free-mode'
import 'swiper/scss/navigation'
import 'swiper/scss/pagination'
import 'swiper/scss/scrollbar'
import { SwiperOptions, VirtualOptions } from 'swiper/types'

import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import horizontalScrollCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/HorizontalScrollCacheStateManager'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'

import NextIcon from '.././../icons/nextIcon.inline.svg'
import ChevronIcon from '../chevronIcon/ChevronIcon'
import Icon from '../icon'

import * as btnStyles from './arrow.module.scss'
import styles from './swiper.module.scss'

// import './swiper.scss' // Moved to _app.tsx
// import * as btnStyles from '../../../../../libraries/design-system/src/components/carousal/sub-components/arrow-sub-component/arrow.module.scss';

export enum DIRECTION {
  NEXT = 'next',
  PREV = 'prev',
}

export interface Loop {
  loop: boolean
  loopedSlides: number
}

export enum ARROW_COLOR {
  WHITE = 'white',
  BLACK = 'black',
}

export enum DOTS_POSITION {
  BOTTOM = 'bottom',
  BOTTOM_HOVER = 'bottom_hover',
  TOP = 'top',
}

export type SwiperRefType = Ref<SwiperRef>

export interface SwiperProps {
  numberOfVisibleSlides?: number
  variableWidth?: boolean
  showNavigator?: boolean
  showDots?: boolean
  className?: string
  slideClassName?: string
  infinite?: Loop
  tracker?: string
  arrowColor?: ARROW_COLOR
  children: ReactNode
  freeMode?: boolean
  dotsPosition?: DOTS_POSITION
  id?: string
  autoplay?: boolean
  breakpoints?: {
    [width: number]: SwiperOptions
    [ratio: string]: SwiperOptions
  }
  isChevronIcon?: boolean
  isVertical?: boolean
  withChildWrapper?: boolean
  disableSwipe?: boolean
  afterChange?: (swiperInstance: SwiperInterface) => void
  navPosition?: NAV_POSITION
  isStickyFreeMode?: boolean
  virtualOptions?: VirtualOptions
  initialSlide?: number
  centeredSlides?: boolean
  prevIconClass?: string
  nextIconClass?: string
  arrowOnClick?: ({ ...args }: { direction: string }) => void
  spaceBetween?: string | number | undefined
  arrowWrapperClass?: string
  swiperId?: string
  isIconWithBG?: boolean
  trackingMetaData?: TrackingMetaData
  wrapperClass?: string
  handleOnScrollEvent?: () => void
  preventInteractionOnTransition?: boolean
  isHorizontalCacheEnabled?: boolean
  renderPrevArrowIcon?: () => ReactNode
  renderNextArrowIcon?: () => ReactNode
}

export enum NAV_POSITION {
  LEFT = 'LEFT',
  RIGHT_WITH_PADDING = 'RIGHT_WITH_PADDING',
  RIGHT = 'RIGHT',
  CENTER = 'center',
  RIGHT_OVER_IMAGE = 'RIGHT_OVER_IMAGE',
  BOTTOM_RIGHT_OVER_IMAGE = 'BOTTOM_RIGHT_OVER_IMAGE',
  RIGHT_WITH_BOTTOM_PADDING = 'RIGHT_WITH_BOTTOM_PADDING',
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
  VERTICAL = 'VERTICAL',
  LEFT_RIGHT = 'LEFT_RIGHT',
}

const SuperSwiper = React.forwardRef<SwiperRef, SwiperProps>(function SuperSwiperEl(
  {
    showDots = false,
    autoplay = false,
    numberOfVisibleSlides = 1,
    variableWidth,
    showNavigator = true,
    arrowColor,
    className,
    slideClassName,
    tracker,
    freeMode = true,
    dotsPosition,
    children,
    id = '',
    breakpoints,
    isChevronIcon = false,
    isVertical = false,
    disableSwipe = false,
    afterChange,
    navPosition,
    infinite,
    isStickyFreeMode = false,
    virtualOptions,
    initialSlide = 0,
    centeredSlides = false,
    prevIconClass = '',
    nextIconClass = '',
    arrowOnClick,
    spaceBetween,
    arrowWrapperClass = '',
    swiperId,
    isIconWithBG = false,
    trackingMetaData,
    wrapperClass,
    handleOnScrollEvent,
    preventInteractionOnTransition,
    isHorizontalCacheEnabled = true,
    renderPrevArrowIcon,
    renderNextArrowIcon,
  }: SwiperProps,
  ref,
) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperInterface>()
  const [totalSlides, setTotalSlides] = useState(0)
  const randomId = useId()
  const randomSelectedId = randomId.split(':')[1]
  const idSuffix = swiperId || randomSelectedId
  const uniqueId = `${id}_${idSuffix}`
  const cacheKey = trackingMetaData?.context?.id ?? id
  const slideIndexCacheKey = `${cacheKey}_activeIndex`
  const currentTranslatePosition = useRef<number>(0)
  const currentActiveIndex = useRef<number>(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)
  const { t } = useTranslation('common')

  const savedTranslatePosition = horizontalScrollCacheStateManager.get<number>(cacheKey)

  const [navData, setNavData] = useState<{
    isBeginning: boolean | undefined
    isEnd: boolean | undefined
  }>({ isBeginning: undefined, isEnd: undefined })

  useEffect(() => {
    return () => {
      isMountedRef.current = false

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      horizontalScrollCacheStateManager.set(cacheKey, currentTranslatePosition.current)
      horizontalScrollCacheStateManager.set(slideIndexCacheKey, currentActiveIndex.current)
    }
  }, [cacheKey, slideIndexCacheKey])

  useEffect(() => {
    setTotalSlides(React.Children.toArray(children).length)
  }, [children])

  useEffect(() => {
    setNavData({ isBeginning: swiperInstance?.isBeginning, isEnd: swiperInstance?.isEnd })
  }, [swiperInstance?.isBeginning, swiperInstance?.isEnd])

  const onPaginationRender = () => {
    if (savedTranslatePosition) return
    setNavData({ isBeginning: swiperInstance?.isBeginning, isEnd: swiperInstance?.isEnd })
  }

  const onSlideChangeTransitionStart = (swiper: SwiperInterface) => {
    if (handleOnScrollEvent) {
      handleOnScrollEvent()
    }
    setNavData({ isBeginning: swiperInstance?.isBeginning, isEnd: swiperInstance?.isEnd })
    currentActiveIndex.current = swiper?.realIndex ?? 0

    if (afterChange) {
      afterChange(swiper)
    }
  }

  const onSetTranslate = (swiper: SwiperInterface) => {
    if (isHorizontalCacheEnabled) {
      currentTranslatePosition.current = swiper.translate
      currentActiveIndex.current = swiper.realIndex ?? 0
    }
  }

  const restoreTranslate = (swiper: SwiperInterface) => {
    if (!isMountedRef.current) {
      return
    }

    const hasSavedTranslatePosition = savedTranslatePosition !== undefined && savedTranslatePosition !== null

    // No cached position — nothing to restore
    if (!hasSavedTranslatePosition) {
      return
    }

    // Layout not measured yet — retry on next frame
    if (swiper.wrapperEl.offsetWidth <= 0) {
      requestAnimationFrame(() => {
        restoreTranslate(swiper)
      })
      return
    }

    swiper.setTranslate(savedTranslatePosition)
    swiper.updateProgress()
    swiper.updateSlidesClasses()
    onSlideChangeTransitionStart(swiper)

    // Restore cached activeIndex and sync pagination dots
    const cachedIndex = horizontalScrollCacheStateManager.get<number>(slideIndexCacheKey)
    if (cachedIndex !== undefined && cachedIndex > 0) {
      swiper.activeIndex = cachedIndex
      swiper.realIndex = cachedIndex
      swiper.snapIndex = cachedIndex
      // Keep ref in sync so unmount saves the correct index
      currentActiveIndex.current = cachedIndex
      if (swiper.pagination) {
        swiper.pagination.update()
      }
    }
  }

  swiperInstance?.pagination.init()
  const wrapperClassNames = classnames(className, styles.wrapper, {
    [styles[`dot_position__${dotsPosition}`]]: showDots,
  })

  const canNavigate = numberOfVisibleSlides < Children.count(children)

  return (
    <>
      <div
        data-testid={id}
        className={classnames(styles.sliderArrows, arrowWrapperClass, {
          [styles.hideSlideArrows]: !showNavigator || totalSlides <= numberOfVisibleSlides,
          [styles.nav__left]: navPosition === NAV_POSITION.LEFT,
          [styles.nav__right_with_padding]: navPosition === NAV_POSITION.RIGHT_WITH_PADDING,
          [styles.nav__right_over_image]: navPosition === NAV_POSITION.RIGHT_OVER_IMAGE,
          [styles.nav__bottom_right_over_image]: navPosition === NAV_POSITION.BOTTOM_RIGHT_OVER_IMAGE,
          [styles.nav__right_with_bottom_padding]: navPosition === NAV_POSITION.RIGHT_WITH_BOTTOM_PADDING,
          [styles.nav__bottom_right]: navPosition === NAV_POSITION.BOTTOM_RIGHT,
          [styles.nav__vertical]: canNavigate && navPosition === NAV_POSITION.VERTICAL,
          [styles.nav__center]: canNavigate && navPosition === NAV_POSITION.CENTER,
          [styles.nav__left_right]: navPosition === NAV_POSITION.LEFT_RIGHT,
        })}
      >
        <PreviousArrow
          className={
            renderPrevArrowIcon ? '' : classnames(styles.prevArrow, styles.navIcon, styles.navIcon__prev, prevIconClass)
          }
          arrowColor={arrowColor}
          disabled={navData.isBeginning}
          tracker={tracker}
          id={`${uniqueId}_left`}
          testid={`${id}_left`}
          ariaLabel={t('previous')}
          isChevronIcon={isChevronIcon}
          isVertical={isVertical}
          onClick={arrowOnClick}
          isIconWithBG={isIconWithBG}
          trackingMetaData={trackingMetaData}
          renderCustomIcon={renderPrevArrowIcon}
        />
        <NextArrow
          className={renderNextArrowIcon ? '' : classnames(styles.nextArrow, styles.navIcon, nextIconClass)}
          arrowColor={arrowColor}
          disabled={navData.isEnd}
          tracker={tracker}
          id={`${uniqueId}_right`}
          testid={`${id}_right`}
          ariaLabel={t('next')}
          isChevronIcon={isChevronIcon}
          isVertical={isVertical}
          onClick={arrowOnClick}
          isIconWithBG={isIconWithBG}
          trackingMetaData={trackingMetaData}
          renderCustomIcon={renderNextArrowIcon}
        />
      </div>

      <SwiperC
        wrapperClass={wrapperClass}
        id={uniqueId}
        autoplay={autoplay}
        breakpoints={breakpoints}
        onPaginationRender={onPaginationRender}
        allowSlideNext={!disableSwipe}
        allowSlidePrev={!disableSwipe}
        className={wrapperClassNames}
        // @i18n-ignore
        direction={isVertical ? 'vertical' : 'horizontal'}
        onSwiper={swiper => {
          setSwiperInstance(swiper)
        }}
        onAfterInit={swiper => {
          if (isHorizontalCacheEnabled) {
            timeoutRef.current = setTimeout(() => {
              requestAnimationFrame(() => {
                restoreTranslate(swiper)
              })
            }, 50)
          }
        }}
        // @i18n-ignore
        slidesPerView={variableWidth ? 'auto' : numberOfVisibleSlides}
        slidesPerGroup={numberOfVisibleSlides}
        mousewheel={{
          forceToAxis: true,
        }}
        centeredSlides={centeredSlides}
        speed={600}
        freeMode={
          freeMode
            ? {
                enabled: freeMode,
                sticky: isStickyFreeMode,
                momentumBounce: true,
                momentumBounceRatio: 1,
                momentumRatio: isStickyFreeMode ? 0.5 : 1,
                momentumVelocityRatio: 1,
              }
            : undefined
        }
        navigation={{
          nextEl: `#${uniqueId}_right`,
          prevEl: `#${uniqueId}_left`,
          enabled: true,
        }}
        pagination={showDots}
        modules={[FreeMode, Navigation, Mousewheel, Pagination, Virtual]}
        loop={infinite?.loop}
        onSlideChangeTransitionStart={onSlideChangeTransitionStart}
        onScrollbarDragStart={onSlideChangeTransitionStart}
        onScroll={onSlideChangeTransitionStart}
        ref={ref}
        virtual={!variableWidth && virtualOptions}
        initialSlide={savedTranslatePosition ? undefined : initialSlide}
        spaceBetween={spaceBetween}
        preventInteractionOnTransition={preventInteractionOnTransition ?? false}
        onSetTranslate={onSetTranslate}
      >
        {React.Children.map(children, (child, childrenIndex) => (
          <SwiperSlide className={classnames(slideClassName, styles.swiperSlide)} virtualIndex={childrenIndex}>
            {child}
          </SwiperSlide>
        ))}
      </SwiperC>
    </>
  )
})

export default SuperSwiper

interface ArrowProps {
  className?: string
  onClick?: (...args: any) => void
  isWhite?: boolean
  tracker?: string
  arrowColor?: ARROW_COLOR
  disabled?: boolean
  id: string
  ariaLabel: string
  isChevronIcon?: boolean
  isVertical?: boolean
  isIconWithBG?: boolean
  trackingMetaData?: TrackingMetaData
  testid?: string
  renderCustomIcon?: () => ReactNode
}

const PreviousArrow = (props: ArrowProps) => {
  return (
    <button
      ref={props.innerRef}
      key={props.id}
      id={props.id}
      data-testid={props.testid}
      className={classnames(props.className, props.isChevronIcon && !props.isVertical && btnStyles.chevPrevButton)}
      aria-label={props.ariaLabel}
      onClick={() => {
        if (props.tracker) {
          trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
            trackGA: true,
            trackInternal: true,
            // @i18n-ignore
            category: 'navigation',
            action: 'click__carousel__left',
            value: props.tracker,
            context: props.trackingMetaData?.context,
          })
        }
        if (props.onClick) {
          props.onClick({
            // @i18n-ignore
            direction: 'prev',
          })
        }
      }}
    >
      {props.isChevronIcon ? (
        <ChevronIcon
          className={classnames(props.isVertical ? btnStyles.vertical : btnStyles.horizontal, btnStyles.prev)}
          isVisible={props.disabled}
          isIconWithBG={props.isIconWithBG}
        />
      ) : props.renderCustomIcon ? (
        props.renderCustomIcon()
      ) : (
        <Icon
          iconSvg={NextIcon}
          className={classnames(styles.navIcon, styles.navIcon__prev, {
            [styles.navIcon__white]: ARROW_COLOR.WHITE === props.arrowColor,
            [styles.navIcon__disabled]: props.disabled,
          })}
        />
      )}
    </button>
  )
}

const NextArrow = (props: ArrowProps) => {
  return (
    <button
      ref={props.innerRef}
      key={props.id}
      id={props.id}
      data-testid={props.testid}
      className={classnames(props.className, props.isChevronIcon && !props.isVertical && btnStyles.chevNextButton)}
      aria-label={props.ariaLabel}
      onClick={() => {
        if (props.tracker) {
          trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
            trackGA: true,
            trackInternal: true,
            // @i18n-ignore
            category: 'navigation',
            action: 'click__carousel__right',
            value: props.tracker,
            context: props.trackingMetaData?.context,
          })
        }
        if (props.onClick) {
          props.onClick({
            // @i18n-ignore
            direction: 'next',
          })
        }
      }}
    >
      {props.isChevronIcon ? (
        <ChevronIcon
          className={classnames(
            props.isVertical ? btnStyles.vertical : btnStyles.horizontal,
            btnStyles.next,
            styles.chevronIcon,
          )}
          // TODO: Swiper is not working well with this class as on drag, it is not updating the state for isBeginning and isEnd, so we are removing it for now
          // isVisible={props.disabled}
          isIconWithBG={props.isIconWithBG}
        />
      ) : props.renderCustomIcon ? (
        props.renderCustomIcon()
      ) : (
        <Icon
          iconSvg={NextIcon}
          className={classnames(styles.navIcon, {
            [styles.navIcon__white]: ARROW_COLOR.WHITE === props.arrowColor,
            [styles.navIcon__disabled]: props.disabled,
          })}
        />
      )}
    </button>
  )
}
